import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { AutoSizer, List } from 'react-virtualized';
import {
  HStack,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPageButton,
  PaginationPrevTrigger,
  PaginationRoot,
} from 'components/ui';
import { RosterPatient } from '../../models/user';
import { isPastTiming, VisitTiming } from './constants';
import {
  HealthCoachDashboardListItem,
  ROW_HEIGHT,
} from './HealthCoachDashboardListItem';
import {
  columnHeaders,
  ROW_GRID,
  ROW_MIN_WIDTH,
  ROW_PADDING_X,
} from './columns';

/**
 * Rows per page once the filtered panel overflows a single page. Below this,
 * the list shows everyone with no pagination controls at all — see the
 * `isPaginated` check below.
 */
const PAGE_SIZE = 100;

/**
 * The patient list.
 *
 * Wrapped in `careos-root` for its thin-scrollbar styling — that class is
 * opt-in (a variable block plus a handful of `.careos-root`-scoped rules), so
 * scoping it here does not restyle the rest of the page.
 *
 * The outer box scrolls horizontally around a min-width floor. Narrow windows
 * would otherwise squeeze the columns until the care-coordination tags (which
 * can't wrap) ran over the chevron; sideways scroll inside the list keeps
 * the rows legible without ever giving the page itself a horizontal scrollbar.
 * AutoSizer measures the floored inner box, so rows render at the same width
 * the header does.
 */
export const HealthCoachDashboardList = ({
  users,
  isPanelEmpty = false,
  appliedVisitTiming = null,
}: {
  users: RosterPatient[];
  /** No patients on the panel at all, rather than none matching the filters. */
  isPanelEmpty?: boolean;
  /**
   * The *applied* appointment-timing filter. Decides whether past appointments
   * are shown, and therefore also what the appointment column is called — see
   * `columnHeaders`. Passed down to each row rather than read from a hook here
   * so the list stays a presentational component.
   */
  appliedVisitTiming?: VisitTiming | null;
}) => {
  const [page, setPage] = useState(1);
  const isPaginated = users.length > PAGE_SIZE;

  // A new filtered result set (a search keystroke, an Apply, a scenario
  // switch) can easily be shorter than the page a user was sitting on —
  // land back on page 1 rather than showing an empty page.
  useEffect(() => {
    setPage(1);
  }, [users]);

  const pagedUsers = useMemo(
    () =>
      isPaginated ? users.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : users,
    [users, page, isPaginated]
  );

  const rowRenderer = ({
    key,
    index,
    style,
  }: {
    key: string;
    index: number;
    style: React.CSSProperties;
  }) => (
    <div key={key} style={style}>
      <HealthCoachDashboardListItem
        user={pagedUsers[index]}
        appliedVisitTiming={appliedVisitTiming}
      />
    </div>
  );

  return (
    // w=100% pins the scroll container to the space the layout gives it;
    // without it the box sizes to its own min-width child and the overflow
    // is clipped by an ancestor instead of scrolling here.
    <Box className="careos-root" w="100%" h="100%" minH={0} overflowX="auto">
      <VStack h="100%" minH={0} minW={`${ROW_MIN_WIDTH}px`} gap={0} align="stretch">
      <Box
        display="grid"
        gridTemplateColumns={ROW_GRID}
        gap={4}
        px={ROW_PADDING_X}
        pb={2}
        borderBottom="1px solid"
        borderColor="cloud"
        flexShrink={0}
      >
        {columnHeaders(isPastTiming(appliedVisitTiming)).map((header, i) => (
          <Text
            key={header || `col-${i}`}
            my={0}
            fontSize="xs"
            fontWeight={700}
            color="secondary.500"
            textTransform="none"
          >
            {header}
          </Text>
        ))}
      </Box>

      {users.length === 0 ? (
        <VStack gap={1} pt={10} align="center">
          <Text my={0} textAlign="center" fontWeight={500}>
            {isPanelEmpty
              ? 'No patients on your panel yet.'
              : 'No patients match these filters.'}
          </Text>
          <Text my={0} textAlign="center" fontSize="sm" color="muted.500">
            {isPanelEmpty
              ? 'Members will appear here once they’re assigned to you.'
              : 'Try clearing a filter in the sidebar.'}
          </Text>
        </VStack>
      ) : (
        <>
          <Box flex={1} minH={0}>
            <AutoSizer>
              {({ height, width }: { height: number; width: number }) => (
                <List
                  width={width}
                  height={height}
                  rowCount={pagedUsers.length}
                  rowHeight={ROW_HEIGHT}
                  rowRenderer={rowRenderer}
                />
              )}
            </AutoSizer>
          </Box>

          {/* Non-scrolling footer sibling, same flex-column pattern as the
              sidebar's Apply Filters footer — no `position: sticky`. Only
              renders once the panel actually overflows a page. */}
          {isPaginated && (
            <HStack
              justifyContent="center"
              pt={3}
              px={ROW_PADDING_X}
              borderTop="1px solid"
              borderColor="cloud"
              flexShrink={0}
            >
              <PaginationRoot
                count={users.length}
                pageSize={PAGE_SIZE}
                page={page}
                onPageChange={(details) => setPage(details.page)}
              >
                <HStack gap={1}>
                  <PaginationPrevTrigger />
                  <PaginationItems
                    render={(item) => (
                      <PaginationPageButton
                        key={item.value}
                        page={item.value}
                        currentPage={page}
                      />
                    )}
                  />
                  <PaginationNextTrigger />
                </HStack>
              </PaginationRoot>
            </HStack>
          )}
        </>
      )}
      </VStack>
    </Box>
  );
};
